import { COUNTRIES } from "@/constants/countries";
import { useThemeColor } from "@/hooks/useThemeColor";
import { VehiclePlate as VehiclePlateType } from "@/types/vehicle-plate";
import { isValidPlateForCountry } from "@/utils/validateVehiclePlate";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { ThemedText } from "../ThemedText";
import { ThemedView } from "../ThemedView";
import VehiclePlate from "./VehiclePlate";

type VehiclePlateInputV2Props = {
  plates: VehiclePlateType[];
  onChange: (
    plates:
      | VehiclePlateType[]
      | ((prev: VehiclePlateType[]) => VehiclePlateType[]),
  ) => void;
  limit: number;
  countries?: { label: string; value: string; flag: string }[];
  defaultCountry?: string;
  onFirstPlateAdded?: () => void;
};

const VehiclePlateInputV2 = forwardRef(
  (
    {
      plates,
      onChange,
      limit = 3,
      countries = COUNTRIES,
      defaultCountry,
      onFirstPlateAdded,
    }: VehiclePlateInputV2Props,
    ref,
  ) => {
    const [inputValue, setInputValue] = useState("");
    const [selectedCountry, setSelectedCountry] = useState(
      defaultCountry || countries[0].value,
    );
    const [modalVisible, setModalVisible] = useState(false);
    const [showInvalidError, setShowInvalidError] = useState(false);
    const inputRef = useRef<TextInput>(null);
    const borderColor = useThemeColor({}, "icon");
    const color = useThemeColor({}, "text");
    const backgroundColor = useThemeColor({}, "background");
    const tintColor = useThemeColor({}, "tint");
    const isFull = plates.length >= limit;
    const { t } = useTranslation();

    useEffect(() => {
      if (defaultCountry) {
        setSelectedCountry(defaultCountry);
      }
    }, [defaultCountry]);

    useEffect(() => {
      const trimmed = inputValue.trim();
      if (!trimmed || isValidPlateForCountry(selectedCountry, trimmed)) {
        setShowInvalidError(false);
      }
    }, [inputValue, selectedCountry]);

    const addPlate = (plateToAdd: string): boolean => {
      if (
        plates.some(
          (p) => p.plate === plateToAdd && p.country === selectedCountry,
        )
      ) {
        Alert.alert(t("DUPLICATE_PLATE"), t("VEHICLE_PLATE_ALREADY_ADDED"));
        return false;
      }
      if (!isValidPlateForCountry(selectedCountry, plateToAdd)) {
        setShowInvalidError(true);
        return false;
      }
      if (plates.length < limit) {
        onChange([...plates, { plate: plateToAdd, country: selectedCountry }]);
        return true;
      }
      return false;
    };

    const handleInputChange = (text: string) => {
      if (isFull) return;

      const sanitizedText = text.toUpperCase().replace(/[^A-Z0-9]/g, "");

      if (sanitizedText.includes(" ")) {
        const parts = sanitizedText.split(/\s+/);
        const newPlates: string[] = parts
          .slice(0, -1)
          .map((p) => p.trim())
          .filter((p) => p.length > 0);
        if (newPlates.length > 0) {
          newPlates.forEach(addPlate);
        }
        setInputValue(parts[parts.length - 1]);
      } else if (sanitizedText.length >= 10) {
        const plate = sanitizedText.slice(0, 10);
        const rest = sanitizedText.slice(10);
        addPlate(plate);
        setInputValue(rest);
      } else {
        setInputValue(sanitizedText);
      }
    };

    const handleSubmitEditing = () => {
      if (isFull || !inputValue.trim()) return;

      const wasEmpty = plates.length === 0;
      if (addPlate(inputValue.trim())) {
        setInputValue("");
        if (wasEmpty) {
          onFirstPlateAdded?.();
        }
      } else {
        inputRef.current?.focus();
      }
    };

    const handleBlur = () => {
      if (isFull) return;
      const trimmed = inputValue.trim();
      if (!trimmed) return;
      if (addPlate(trimmed)) {
        setInputValue("");
      }
    };

    useImperativeHandle(ref, () => ({
      submit: (): boolean => {
        const trimmed = inputValue.trim();
        if (!trimmed) return true;
        if (addPlate(trimmed)) {
          setInputValue("");
          return true;
        }
        inputRef.current?.focus();
        return false;
      },
      reset: () => {
        setInputValue("");
        setShowInvalidError(false);
      },
    }));

    const selectedCountryData = COUNTRIES.find(
      (c) => c.value === selectedCountry,
    );

    const removePlateAt = (index: number) => {
      onChange(plates.filter((_, i) => i !== index));
    };

    const CountryPickerModal = () =>
      createPortal(
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000,
          }}
          onClick={() => setModalVisible(false)}
        >
          <div
            style={{
              backgroundColor,
              borderRadius: 20,
              padding: 10,
              width: "90%",
              maxWidth: 400,
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ maxHeight: 300, overflowY: "auto" }}>
              {countries.map((item) => (
                <TouchableOpacity
                  key={item.value}
                  style={styles.countryItem}
                  onPress={() => {
                    setSelectedCountry(item.value);
                    setModalVisible(false);
                  }}
                >
                  <ThemedText style={styles.countryFlag}>
                    {item.flag}
                  </ThemedText>
                  <ThemedText style={styles.countryName}>
                    {item.label}
                  </ThemedText>
                </TouchableOpacity>
              ))}
            </div>
          </div>
        </div>,
        document.body,
      );

    return (
      <ThemedView style={styles.container}>
        <div
          style={{
            borderBottom: `1px solid ${borderColor}`,
            paddingBottom: 4,
            overflowX: "auto",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 4,
              whiteSpace: "nowrap",
            }}
          >
            {plates.map((p, idx) => (
              <ThemedView
                key={`${p.country}.${p.plate}`}
                style={styles.chip}
              >
                <VehiclePlate
                  plate={p.plate}
                  country={p.country}
                  onDelete={() => removePlateAt(idx)}
                />
              </ThemedView>
            ))}
            {!isFull && (
              <>
                <TouchableOpacity
                  testID="plate-country-flag"
                  style={styles.flagButton}
                  onPress={() => setModalVisible(true)}
                >
                  <ThemedText style={styles.flagText}>
                    {selectedCountryData?.flag}
                  </ThemedText>
                </TouchableOpacity>
                <TextInput
                  ref={inputRef}
                  testID="plate-input"
                  autoFocus
                  submitBehavior="submit"
                  style={
                    [
                      styles.input,
                      { color },
                      { outlineStyle: "none", caretColor: tintColor },
                    ] as any
                  }
                  placeholder={`${
                    selectedCountryData?.vehiclePlatePlaceholder ||
                    t("VEHICLE_PLATES_PLACEHOLDER")
                  } (${t("ONBOARDING_OPTIONAL")})`}
                  placeholderTextColor={borderColor}
                  value={inputValue}
                  onChangeText={handleInputChange}
                  onSubmitEditing={handleSubmitEditing}
                  onBlur={handleBlur}
                  returnKeyType="done"
                  autoCapitalize="characters"
                />
              </>
            )}
          </div>
        </div>
        <View style={styles.progressLinesContainer}>
          {Array.from({ length: limit }).map((_, i) => (
            <View
              key={i}
              style={[
                styles.progressLine,
                i < plates.length && styles.progressLineActive,
              ]}
            />
          ))}
        </View>
        {showInvalidError && (
          <ThemedText testID="plate-invalid-error" style={styles.errorText}>
            {t("INVALID_PLATE_FORMAT_HINT", {
              country: selectedCountryData?.label ?? selectedCountry,
            })}
          </ThemedText>
        )}
        {modalVisible && <CountryPickerModal />}
      </ThemedView>
    );
  },
);

const styles = StyleSheet.create({
  container: {
    flexDirection: "column",
    marginBottom: 16,
    position: "relative",
  },
  chip: {
    flexShrink: 0,
  },
  flagButton: {
    paddingVertical: 8,
    paddingHorizontal: 6,
    flexShrink: 0,
  },
  flagText: {
    fontSize: 24,
  },
  input: {
    flexShrink: 0,
    minWidth: 100,
    paddingVertical: 8,
    fontSize: 18,
    borderWidth: 0,
    backgroundColor: "transparent",
  },
  errorText: {
    color: "red",
    fontSize: 12,
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
  },
  progressLinesContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginTop: 8,
  },
  progressLine: {
    flex: 1,
    height: 4,
    borderRadius: 999,
    backgroundColor: "#d9d9d9",
  },
  progressLineActive: {
    backgroundColor: "#0a7ea4",
  },
  countryItem: {
    flexDirection: "row",
    alignItems: "center",
    padding: 10,
  },
  countryFlag: {
    fontSize: 24,
    marginRight: 10,
  },
  countryName: {
    fontSize: 18,
  },
});

VehiclePlateInputV2.displayName = "VehiclePlateInputV2";

export default VehiclePlateInputV2;
